"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useThemeOptional } from "../contexts/ThemeContext";
import { useDesktopContext } from "../contexts/DesktopContext";
import { WINDOW_ID } from "../apps/registry";
import { isWindowClosed } from "../types";
import { scopedKey } from "../lib/scopedStorage";

const SESSION_START_KEY = "desktopui:session-started-at";

function getSessionStart(): number {
  if (typeof window === "undefined") return Date.now();
  try {
    const stored = window.sessionStorage.getItem(SESSION_START_KEY);
    if (stored) {
      const n = Number(stored);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const now = Date.now();
    window.sessionStorage.setItem(SESSION_START_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

/* ==========================================================================
   TYPES
   ========================================================================== */

interface WidgetPosition {
  x: number;
  y: number;
}

interface WidgetSize {
  width: number;
  height: number;
}

interface WidgetLayout {
  position: WidgetPosition;
  size: WidgetSize;
  visible: boolean;
}

interface WidgetConfig {
  id: string;
  title: string;
  defaultSize: WidgetSize;
  minSize: WidgetSize;
  component: React.FC<{ onActivate?: () => void }>;
}

type WidgetLayouts = Record<string, WidgetLayout>;

/* ==========================================================================
   PERSISTENCE
   ========================================================================== */

const STORAGE_KEY_BASE = "widget-layer-layouts-v3";
const storageKey = () => scopedKey(STORAGE_KEY_BASE);

function loadLayouts(): WidgetLayouts | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function saveLayouts(layouts: WidgetLayouts) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(layouts));
  } catch {
    // ignore
  }
}

/* ==========================================================================
   CLOCK WIDGET
   ========================================================================== */

export function ClockWidget({ onActivate }: { onActivate?: () => void } = {}) {
  const [now, setNow] = useState(new Date());
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;
  const { openWindow } = useDesktopContext();

  const handleOpenApp = () => {
    openWindow(WINDOW_ID.stopwatch);
    onActivate?.();
  };

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const timeStr = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateStr = now.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Analog clock hands
  const hourDeg = (hours % 12) * 30 + minutes * 0.5;
  const minuteDeg = minutes * 6 + seconds * 0.1;
  const secondDeg = seconds * 6;

  const faceColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";
  const markColor = isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)";
  const handColor = isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)";
  const secondColor = "#ef4444";
  const textColor = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";
  const subTextColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)";

  return (
    <div
      className="flex h-full cursor-pointer select-none flex-col items-center justify-center gap-3 p-4"
      onClick={handleOpenApp}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpenApp();
        }
      }}
    >
      {/* Analog clock */}
      <div
        className="relative flex-shrink-0 rounded-full"
        style={{
          width: 120,
          height: 120,
          backgroundColor: faceColor,
          border: `2px solid ${markColor}`,
        }}
      >
        {/* Hour markers */}
        {Array.from({ length: 12 }, (_, i) => {
          const angle = i * 30;
          const isMain = i % 3 === 0;
          return (
            <div
              key={i}
              className="absolute"
              style={{
                width: isMain ? 2 : 1,
                height: isMain ? 10 : 6,
                backgroundColor: markColor,
                top: isMain ? 6 : 8,
                left: "50%",
                transformOrigin: `50% ${60 - (isMain ? 6 : 8)}px`,
                transform: `translateX(-50%) rotate(${angle}deg)`,
              }}
            />
          );
        })}
        {/* Hour hand */}
        <div
          className="absolute rounded-full"
          style={{
            width: 3,
            height: 30,
            backgroundColor: handColor,
            bottom: "50%",
            left: "50%",
            transformOrigin: "50% 100%",
            transform: `translateX(-50%) rotate(${hourDeg}deg)`,
          }}
        />
        {/* Minute hand */}
        <div
          className="absolute rounded-full"
          style={{
            width: 2,
            height: 40,
            backgroundColor: handColor,
            bottom: "50%",
            left: "50%",
            transformOrigin: "50% 100%",
            transform: `translateX(-50%) rotate(${minuteDeg}deg)`,
          }}
        />
        {/* Second hand */}
        <div
          className="absolute"
          style={{
            width: 1,
            height: 44,
            backgroundColor: secondColor,
            bottom: "calc(50% - 8px)",
            left: "50%",
            transformOrigin: "50% calc(100% - 8px)",
            transform: `translateX(-50%) rotate(${secondDeg}deg)`,
          }}
        />
        {/* Center dot */}
        <div
          className="absolute rounded-full"
          style={{
            width: 6,
            height: 6,
            backgroundColor: secondColor,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>

      <div className="text-center">
        <div
          className="text-2xl font-light tracking-wide"
          style={{ color: textColor }}
        >
          {timeStr}
        </div>
        <div className="mt-0.5 text-xs" style={{ color: subTextColor }}>
          {dateStr}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   CALENDAR WIDGET
   ========================================================================== */

export function CalendarWidget({
  onActivate,
}: { onActivate?: () => void } = {}) {
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;
  const [viewDate, setViewDate] = useState(new Date());

  const handleOpenApp = () => {
    // No Calendar app in this build — the widget is display-only.
    onActivate?.();
  };

  const today = new Date();
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const monthName = viewDate.toLocaleDateString([], {
    month: "long",
    year: "numeric",
  });
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const textColor = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";
  const subColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)";
  const todayBg = isDark ? "#3b82f6" : "#2563eb";

  const isToday = (day: number) =>
    day === today.getDate() &&
    month === today.getMonth() &&
    year === today.getFullYear();

  return (
    <div
      className="flex h-full cursor-pointer select-none flex-col p-3"
      style={{ color: textColor }}
      onClick={handleOpenApp}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpenApp();
        }
      }}
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <button
          onClick={(e) => {
            e.stopPropagation();
            prevMonth();
          }}
          className="flex h-6 w-6 items-center justify-center rounded text-xs hover:bg-white/10"
          style={{ color: subColor }}
        >
          &lt;
        </button>
        <span className="text-xs font-semibold">{monthName}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            nextMonth();
          }}
          className="flex h-6 w-6 items-center justify-center rounded text-xs hover:bg-white/10"
          style={{ color: subColor }}
        >
          &gt;
        </button>
      </div>

      {/* Day headers */}
      <div className="mb-1 grid grid-cols-7 gap-0">
        {dayNames.map((d) => (
          <div
            key={d}
            className="py-0.5 text-center text-[10px] font-medium"
            style={{ color: subColor }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid flex-1 grid-cols-7 gap-0">
        {Array.from({ length: firstDay }, (_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          return (
            <div
              key={day}
              className="flex items-center justify-center rounded-full text-[11px]"
              style={{
                width: 24,
                height: 24,
                margin: "0 auto",
                backgroundColor: isToday(day) ? todayBg : "transparent",
                color: isToday(day) ? "#fff" : textColor,
                fontWeight: isToday(day) ? 600 : 400,
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ==========================================================================
   SYSTEM STATS WIDGET
   ========================================================================== */

export function SystemStatsWidget({
  onActivate,
}: { onActivate?: () => void } = {}) {
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;
  const { windows } = useDesktopContext();
  const [now, setNow] = useState(() => Date.now());
  const [memoryMb, setMemoryMb] = useState(0);

  // Tick once a minute — uptime is shown at minute precision.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // JS heap polling — separate from window-count, which is reactive via context.
  useEffect(() => {
    const readMemory = () => {
      const mem = (performance as { memory?: { usedJSHeapSize: number } })
        .memory;
      if (mem) setMemoryMb(Math.round(mem.usedJSHeapSize / 1_048_576));
    };
    readMemory();
    const id = setInterval(readMemory, 5000);
    return () => clearInterval(id);
  }, []);

  const handleOpenApp = () => {
    // No Activity Monitor app in this build — the widget is display-only.
    onActivate?.();
  };

  // Live, reactive: counts non-closed windows (open + minimized) from context.
  const openWindowCount = useMemo(() => {
    let n = 0;
    windows.forEach((w) => {
      if (!isWindowClosed(w.state)) n++;
    });
    return n;
  }, [windows]);

  const uptimeMins = useMemo(() => {
    const start = getSessionStart();
    return Math.max(0, Math.floor((now - start) / 60_000));
  }, [now]);

  const textColor = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";
  const subColor = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.4)";
  const barBg = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";

  const uptimeStr =
    uptimeMins < 60
      ? `${uptimeMins}m`
      : `${Math.floor(uptimeMins / 60)}h ${uptimeMins % 60}m`;

  return (
    <div
      className="flex h-full cursor-pointer select-none flex-col gap-3 p-3"
      style={{ color: textColor }}
      onClick={handleOpenApp}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpenApp();
        }
      }}
    >
      <div className="text-xs font-semibold">System</div>

      {/* Uptime */}
      <div>
        <div className="mb-1 flex justify-between text-[11px]">
          <span style={{ color: subColor }}>Uptime</span>
          <span>{uptimeStr}</span>
        </div>
      </div>

      {/* Open Windows */}
      <div>
        <div className="mb-1 flex justify-between text-[11px]">
          <span style={{ color: subColor }}>Windows</span>
          <span>{openWindowCount}</span>
        </div>
      </div>

      {/* Memory */}
      {memoryMb > 0 && (
        <div>
          <div className="mb-1 flex justify-between text-[11px]">
            <span style={{ color: subColor }}>JS Heap</span>
            <span>{memoryMb} MB</span>
          </div>
          <div
            className="h-1.5 overflow-hidden rounded-full"
            style={{ backgroundColor: barBg }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, (memoryMb / 512) * 100)}%`,
                backgroundColor:
                  memoryMb > 400
                    ? "#ef4444"
                    : memoryMb > 200
                      ? "#f59e0b"
                      : "#22c55e",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   WIDGET FRAME (draggable, resizable wrapper)
   ========================================================================== */

function WidgetFrame({
  id,
  title,
  layout,
  minSize,
  isDark,
  onMove,
  onResize,
  onToggleVisibility,
  children,
}: {
  id: string;
  title: string;
  layout: WidgetLayout;
  minSize: WidgetSize;
  isDark: boolean;
  onMove: (id: string, pos: WidgetPosition) => void;
  onResize: (id: string, size: WidgetSize) => void;
  onToggleVisibility: (id: string) => void;
  children: React.ReactNode;
}) {
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - layout.position.x,
        y: e.clientY - layout.position.y,
      };

      const handleMove = (e: MouseEvent) => {
        if (!isDragging.current) return;
        onMove(id, {
          x: e.clientX - dragOffset.current.x,
          y: e.clientY - dragOffset.current.y,
        });
      };

      const handleUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
    },
    [id, layout.position, onMove]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isResizing.current = true;
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = layout.size.width;
      const startH = layout.size.height;

      const handleMove = (e: MouseEvent) => {
        if (!isResizing.current) return;
        onResize(id, {
          width: Math.max(minSize.width, startW + (e.clientX - startX)),
          height: Math.max(minSize.height, startH + (e.clientY - startY)),
        });
      };

      const handleUp = () => {
        isResizing.current = false;
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.body.style.cursor = "nwse-resize";
      document.body.style.userSelect = "none";
    },
    [id, layout.size, minSize, onResize]
  );

  if (!layout.visible) return null;

  const bg = isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.18)";
  const borderColor = isDark
    ? "rgba(255, 255, 255, 0.12)"
    : "rgba(255, 255, 255, 0.35)";
  const headerColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)";

  return (
    <div
      className="absolute flex flex-col overflow-hidden"
      style={{
        left: layout.position.x,
        top: layout.position.y,
        width: layout.size.width,
        height: layout.size.height,
        borderRadius: 16,
        backgroundColor: bg,
        border: `1px solid ${borderColor}`,
        backdropFilter: "blur(60px) saturate(1.4)",
        WebkitBackdropFilter: "blur(60px) saturate(1.4)",
        boxShadow: isDark
          ? "0 2px 16px rgba(0,0,0,0.2), inset 0 0.5px 0 rgba(255,255,255,0.05)"
          : "0 2px 16px rgba(0,0,0,0.06), inset 0 0.5px 0 rgba(255,255,255,0.4)",
      }}
    >
      {/* Header (drag handle) */}
      <div
        className="flex flex-shrink-0 cursor-grab items-center justify-between px-3 py-1 active:cursor-grabbing"
        onMouseDown={handleDragStart}
        style={{
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
        }}
      >
        <span
          className="text-[11px] font-semibold"
          style={{
            color: headerColor,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {title}
        </span>
        <button
          onClick={() => onToggleVisibility(id)}
          className="flex h-4 w-4 items-center justify-center rounded-full text-[10px] hover:bg-white/10"
          style={{ color: headerColor }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">{children}</div>

      {/* Resize handle */}
      <div
        className="absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize"
        onMouseDown={handleResizeStart}
        style={{ opacity: 0 }}
      />
    </div>
  );
}

/* ==========================================================================
   WIDGET LAYER COMPONENT
   ========================================================================== */

const WIDGETS: WidgetConfig[] = [
  {
    id: "clock",
    title: "Clock",
    defaultSize: { width: 180, height: 260 },
    minSize: { width: 150, height: 220 },
    component: ClockWidget,
  },
  {
    id: "calendar",
    title: "Calendar",
    defaultSize: { width: 220, height: 260 },
    minSize: { width: 190, height: 230 },
    component: CalendarWidget,
  },
  {
    id: "system",
    title: "System",
    defaultSize: { width: 180, height: 180 },
    minSize: { width: 150, height: 140 },
    component: SystemStatsWidget,
  },
];

const DOCK_BOTTOM_RESERVE = 100; // dock + breathing room
const MENU_BAR_TOP = 28;

function getDefaultLayouts(): WidgetLayouts {
  const layouts: WidgetLayouts = {};
  const gap = 20;
  const topPadding = MENU_BAR_TOP + gap;
  const leftPadding = 40;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 900;
  const maxY = viewportHeight - DOCK_BOTTOM_RESERVE;

  let col = 0;
  let y = topPadding;
  let colX = leftPadding;
  let colMaxWidth = 0;

  for (const w of WIDGETS) {
    // If this widget would overflow the viewport, start a new column
    if (
      (y + w.defaultSize.height > maxY && col > 0) ||
      (y !== topPadding && y + w.defaultSize.height > maxY)
    ) {
      col++;
      colX += colMaxWidth + gap;
      y = topPadding;
      colMaxWidth = 0;
    }

    layouts[w.id] = {
      position: { x: colX, y },
      size: { ...w.defaultSize },
      visible: true,
    };

    colMaxWidth = Math.max(colMaxWidth, w.defaultSize.width);
    y += w.defaultSize.height + gap;
  }
  return layouts;
}

function clampLayouts(layouts: WidgetLayouts): WidgetLayouts {
  if (typeof window === "undefined") return layouts;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const minY = MENU_BAR_TOP + 10;
  const maxY = vh - DOCK_BOTTOM_RESERVE;
  const result: WidgetLayouts = {};
  for (const [id, layout] of Object.entries(layouts)) {
    const x = Math.max(0, Math.min(layout.position.x, vw - layout.size.width));
    const y = Math.max(
      minY,
      Math.min(layout.position.y, maxY - layout.size.height)
    );
    result[id] = { ...layout, position: { x, y } };
  }
  return result;
}

interface WidgetLayerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WidgetLayer({ isOpen, onClose }: WidgetLayerProps) {
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;

  const [layouts, setLayouts] = useState<WidgetLayouts>(() => {
    const saved = loadLayouts();
    if (saved) return clampLayouts(saved);
    return getDefaultLayouts();
  });

  // Persist layouts
  useEffect(() => {
    saveLayouts(layouts);
  }, [layouts]);

  // Clamp positions when viewport resizes
  useEffect(() => {
    const handleResize = () => {
      setLayouts((prev) => clampLayouts(prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const handleMove = useCallback((id: string, pos: WidgetPosition) => {
    setLayouts((prev) => {
      const layout = prev[id];
      if (!layout) return prev;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const minY = MENU_BAR_TOP + 10;
      const maxY = vh - DOCK_BOTTOM_RESERVE;
      return {
        ...prev,
        [id]: {
          ...layout,
          position: {
            x: Math.max(0, Math.min(pos.x, vw - layout.size.width)),
            y: Math.max(minY, Math.min(pos.y, maxY - layout.size.height)),
          },
        },
      };
    });
  }, []);

  const handleResize = useCallback((id: string, size: WidgetSize) => {
    setLayouts((prev) => {
      const layout = prev[id];
      if (!layout) return prev;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxY = vh - DOCK_BOTTOM_RESERVE;
      const clampedW = Math.min(size.width, vw - layout.position.x);
      const clampedH = Math.min(size.height, maxY - layout.position.y);
      return {
        ...prev,
        [id]: { ...layout, size: { width: clampedW, height: clampedH } },
      };
    });
  }, []);

  const handleToggleVisibility = useCallback((id: string) => {
    setLayouts((prev) => ({
      ...prev,
      [id]: { ...prev[id], visible: !prev[id].visible },
    }));
  }, []);

  const handleShowAll = useCallback(() => {
    setLayouts((prev) => {
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        next[id] = { ...next[id], visible: true };
      }
      return next;
    });
  }, []);

  const hiddenWidgets = useMemo(
    () => WIDGETS.filter((w) => layouts[w.id] && !layouts[w.id].visible),
    [layouts]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[50]">
      {/* Click backdrop to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Header bar */}
      <div className="absolute left-0 right-0 top-7 z-10 flex items-center justify-between px-4 py-2">
        <span
          className="text-sm font-semibold"
          style={{
            color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
          }}
        >
          Widgets
        </span>
        <div className="flex items-center gap-2">
          {hiddenWidgets.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShowAll();
              }}
              className="rounded-md px-2 py-1 text-xs transition-colors"
              style={{
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.08)",
                color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
              }}
            >
              Show All ({hiddenWidgets.length})
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xs transition-colors"
            style={{
              backgroundColor: isDark
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.08)",
              color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
            }}
          >
            Done
          </button>
        </div>
      </div>

      {/* Widgets */}
      {WIDGETS.map((widget) => {
        const layout = layouts[widget.id];
        if (!layout) return null;
        const Comp = widget.component;
        return (
          <WidgetFrame
            key={widget.id}
            id={widget.id}
            title={widget.title}
            layout={layout}
            minSize={widget.minSize}
            isDark={isDark}
            onMove={handleMove}
            onResize={handleResize}
            onToggleVisibility={handleToggleVisibility}
          >
            <Comp onActivate={onClose} />
          </WidgetFrame>
        );
      })}
    </div>
  );
}
